import React, { useEffect, useRef, useCallback, useState } from 'react';
import { IconClose, IconBack } from './icons';
import { buildThemeVariables, useResolvedThemeMode } from '../theme';
import type { PortfolioConnectTheme } from '../types';
import { GlobalWidgetStyles } from './ui';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  children: React.ReactNode;
  width?: number;
  isSandbox?: boolean;
  theme?: PortfolioConnectTheme;
  /**
   * When set, clicking outside the modal triggers a `window.confirm`
   * with this prompt instead of closing immediately. The Escape key and
   * the explicit close button still close without prompting (those are
   * deliberate user actions). Used to protect mid-flow states like
   * upload-in-progress or inbound-email polling from accidental loss.
   */
  confirmOnOverlayClose?: string;
}

const MOBILE_BREAKPOINT = 640;
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

/**
 * Subscribes to a `(max-width: …px)` media query so the modal switches
 * between full-screen-mobile and centred-desktop layouts when the
 * viewport changes (rotation, browser resize, dev-tools toggle).
 * Without this, `isMobile` was captured once at first render and the
 * modal would stay in its original layout forever.
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (media.addEventListener) media.addEventListener('change', handler);
    else media.addListener(handler);
    // Sync once in case the initial value drifted (SSR mount).
    setIsMobile(media.matches);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', handler);
      else media.removeListener(handler);
    };
  }, []);
  return isMobile;
}

/**
 * Returns true if the element is *visually* present (not hidden by
 * `display: none`, `visibility: hidden`, or being inside a hidden
 * ancestor). `offsetParent !== null` was the previous heuristic but
 * returns null for `position: fixed` elements — which the modal IS,
 * so any fixed-positioned focusable inside the modal would silently
 * disappear from the focus trap.
 */
function isVisuallyFocusable(el: HTMLElement): boolean {
  if (el.hasAttribute('disabled')) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.tabIndex < 0) return false;
  // getClientRects().length === 0 → not rendered. Works for fixed/sticky
  // elements and is cheaper than a full getComputedStyle() check.
  return el.getClientRects().length > 0;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onBack,
  showBackButton = false,
  children,
  width = 440,
  isSandbox = false,
  theme,
  confirmOnOverlayClose,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Simple focus trap — intercepts Tab to cycle within the modal.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const enabled = Array.from(focusables).filter(isVisuallyFocusable);
        if (enabled.length === 0) return;
        const first = enabled[0];
        const last = enabled[enabled.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    // Move focus into the modal on open so SR users know context shifted.
    requestAnimationFrame(() => {
      const target =
        modalRef.current?.querySelector<HTMLElement>('h2, h3, [data-pc-autofocus]') ||
        modalRef.current;
      target?.focus();
    });

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, handleKeyDown]);

  // Hooks below must run on every render — placed above the early
  // `if (!isOpen) return null` to satisfy Rules of Hooks.
  // Subscribe to OS theme changes so `theme.mode: 'auto'` actually
  // restyles live when the user toggles their system theme. For 'light'
  // / 'dark' the hook returns the static value.
  const resolvedMode = useResolvedThemeMode(theme?.mode);
  const isMobile = useIsMobile();

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== overlayRef.current) return;
    if (confirmOnOverlayClose) {
      // window.confirm is intentionally synchronous — for an SDK widget
      // this is a deliberate trade-off (matches browser native dialogs
      // like beforeunload) over building a custom modal-on-modal.
      const ok = typeof window !== 'undefined' && window.confirm(confirmOnOverlayClose);
      if (!ok) return;
    }
    onClose();
  };

  const themeVars = buildThemeVariables(theme, resolvedMode);

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={isSandbox ? 'Portfolio Connect (Sandbox)' : 'Portfolio Connect'}
      className="pc-widget-root"
      style={{
        ...themeVars,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--pc-color-overlay)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? 0 : 16,
        fontFamily: 'var(--pc-font-family)',
      }}
    >
      <GlobalWidgetStyles />
      <div
        ref={modalRef}
        className="pc-fade-in"
        tabIndex={-1}
        style={{
          backgroundColor: 'var(--pc-color-surface)',
          borderRadius: isMobile ? 0 : 'var(--pc-radius-xl)',
          width: '100%',
          maxWidth: isMobile ? '100%' : width,
          maxHeight: isMobile ? '100dvh' : '90vh',
          height: isMobile ? '100dvh' : 'auto',
          overflow: 'auto',
          position: 'relative',
          boxShadow: 'var(--pc-shadow-card)',
          color: 'var(--pc-color-text)',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        {isSandbox && (
          <div
            style={{
              backgroundColor: 'var(--pc-color-warning-bg)',
              borderBottom: '1px solid var(--pc-color-warning-border)',
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--pc-color-warning)',
              textAlign: 'center',
              letterSpacing: 0.2,
            }}
          >
            Sandbox mode — using test data
          </div>
        )}

        {showBackButton && onBack && (
          <button
            onClick={onBack}
            style={{
              position: 'absolute',
              top: isSandbox ? 38 : 10,
              left: 10,
              background: 'none',
              border: 'none',
              padding: 8,
              cursor: 'pointer',
              color: 'var(--pc-color-text-muted)',
              borderRadius: 'var(--pc-radius-md)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = 'var(--pc-color-surface-subtle)')
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label="Back"
          >
            <IconBack size={20} />
          </button>
        )}

        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: isSandbox ? 38 : 10,
            right: 10,
            background: 'none',
            border: 'none',
            padding: 8,
            cursor: 'pointer',
            color: 'var(--pc-color-text-muted)',
            borderRadius: 'var(--pc-radius-md)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            transition: 'background-color 0.15s',
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--pc-color-surface-subtle)')
          }
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          aria-label="Close"
        >
          <IconClose size={20} />
        </button>
        {children}
      </div>
    </div>
  );
};
